import { TX_TYPES } from '@exodus/activity-txs/redux/constants/tx-types';
// eslint-disable-next-line @exodus/restricted-imports/prefer-basic-utils
import { memoize } from 'lodash';
import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import type { Assets } from '@/types';
import selectors from '@/ui/flux/selectors';
import type { ActivityItem } from '../types.js';
import { getIcon, getTitles, getValue } from '../utils.js';

// once NFTs will be integrated we can use selectors.activityTxs.createFullActivity instead.
const createFullActivitySelector = selectors.activityTxs.createWithFiatActivity(
  {
    createActivitySelector: selectors.activityTxs.createAssetSourceBaseActivity,
  },
) as (params: {
  assetName: string;
  walletAccount: string;
  displayOrderWithoutTx?: boolean;
}) => Selector<unknown, ActivityItem[]>;

type CreateFilteredAssetSourceActivitySelectorParams = {
  assetName: string;
  walletAccount: string;
  type: string;
};

const createFilteredAssetSourceActivitySelector = memoize(
  ({
    assetName,
    walletAccount,
    type,
  }: CreateFilteredAssetSourceActivitySelectorParams) =>
    createSelector(
      createFullActivitySelector({
        assetName,
        walletAccount,
        displayOrderWithoutTx: true,
      }),
      (activity) => {
        const types: string[] = Array.isArray(type) ? type : [type];
        const filteredActivity: ActivityItem[] = [];

        if (types.includes('all')) {
          return activity;
        }

        for (const activityItem of activity) {
          if (types.includes('non-token')) {
            const { tx } = activityItem;

            if (tx && tx.tokens?.length === 0) {
              filteredActivity.push(activityItem);
              continue;
            }
          }

          if (
            types.includes('exchanged') &&
            activityItem.type === TX_TYPES.EXCHANGE
          ) {
            filteredActivity.push(activityItem);
            continue;
          }

          if (
            types.includes('received') &&
            !activityItem.sent &&
            activityItem.type !== TX_TYPES.EXCHANGE
          ) {
            filteredActivity.push(activityItem);
            continue;
          }

          if (
            types.includes('sent') &&
            activityItem.sent &&
            activityItem.type !== TX_TYPES.EXCHANGE
          ) {
            filteredActivity.push(activityItem);
          }
        }

        return filteredActivity;
      },
    ),
  ({ assetName, walletAccount, type }) =>
    `${assetName}_${walletAccount}_${type}`,
);

type CreateAssetSourceActivitySelectorParams = {
  assetName: string;
  walletAccount: string;
  type?: string;
};

const createAssetSourceActivitySelector = memoize(
  ({
    assetName,
    walletAccount,
    type,
  }: CreateAssetSourceActivitySelectorParams) => {
    const activitySelector = type
      ? createFilteredAssetSourceActivitySelector({
          assetName,
          walletAccount,
          type,
        })
      : createFullActivitySelector({
          assetName,
          walletAccount,
          displayOrderWithoutTx: true,
        });

    return createSelector(
      activitySelector,
      selectors.assets.all,
      (activity: ActivityItem[], assets: Assets) =>
        activity.map((activityItem): ActivityItem => {
          // We have to use `getValue` on client, cannot move this in hydra until assets will define api for operation.
          const displayAmount =
            activityItem.displayAmount ||
            (activityItem.tx ? getValue(activityItem.tx) : null);
          const titles = getTitles(activityItem, assets);
          const icon = getIcon(activityItem);

          return {
            ...activityItem,
            ...titles,
            displayAmount,
            icon,
          };
        }),
    );
  },
  ({ assetName, walletAccount, type }) =>
    `${assetName}_${walletAccount}_${type}`,
);

export default createAssetSourceActivitySelector;
